import { TASK_TYPE_INFO } from "@krut/shared";

/**
 * An audit row's details in words for Admin → Audit log: "Group: Sales · Browser: on → off" instead of the
 * JSON the server stored (the page keeps that one click away). Works for any action: known keys get the
 * app's own names, anything else is worded from its key.
 */
export interface AuditRow {
  action: string;
  actorId: string | null;
  target: string | null;
  details: Record<string, unknown>;
}
export interface AuditPart {
  label: string;
  value: string;
}
/** Who or what an id is, for the people and groups the page has loaded (so details say "Group: Sales", not an id). */
export type AuditLookup = (id: string) => { label: string; name: string } | undefined;

/** Names as the app shows them elsewhere (a group's Access and Budget tabs, Branding); other keys are worded from the key. */
const LABELS: Record<string, string> = {
  terminal: "Terminal & code",
  webTools: "Web search & fetch",
  cronJobs: "Scheduled jobs",
  fileUpload: "File uploads",
  customMcp: "Own MCP servers",
  publicApps: "Apps outside the company",
  composioToolkits: "Connected apps",
  publishSkills: "Skill sharing",
  promoteMemory: "Memory sharing",
  whatsapp: "WhatsApp",
  monthlyBudgetUsd: "Monthly budget",
  storageGb: "Storage",
  memoryMb: "Assistant memory",
  sandboxMemoryMb: "Sandbox memory",
  appsMemoryMb: "Web apps memory",
  isDefault: "Default group",
  groupId: "Group",
  toolkit: "App",
  accentColor: "Accent colour",
  sidebarColor: "Sidebar colour",
  ip: "IP address",
  url: "URL",
  baseUrl: "Base URL",
  clientId: "Client ID",
  tenantId: "Tenant ID",
  expiresAt: "Expires",
  others: "Other sessions",
};
/** Facts rather than switches: yes/no, where a switch reads on/off ("Browser: on → off"). */
const YES_NO = new Set(["shared", "company", "self", "others", "dataKept", "archived", "private", "newShareLink", "isDefault"]);
/** Internal ids the readable line can do without (the target already names the thing; the JSON still has them). */
const HIDDEN = new Set(["serverId", "promotionId", "request"]);
const mb = (n: number) => `${n.toLocaleString()} MB`;
const UNITS: Record<string, (n: number) => string> = {
  monthlyBudgetUsd: (n) => `$${n.toLocaleString()}`,
  storageGb: (n) => `${n.toLocaleString()} GB`,
  memoryMb: mb,
  sandboxMemoryMb: mb,
  appsMemoryMb: mb,
  minutes: (n) => `${n.toLocaleString()} min`,
};
const NAMES: Record<string, string> = { slack: "Slack", discord: "Discord", whatsapp: "WhatsApp", google: "Google", microsoft: "Microsoft" };
/** Sharing levels, worded as on a group's Knowledge tab. */
const LEVELS: Record<string, string> = { off: "off", group: "within the group", company: "group and company" };
const TASKS: Record<string, { label: string } | undefined> = TASK_TYPE_INFO;

/** What the target is, for actions whose target is a name rather than an id (the first match wins). */
const TARGETS: [action: string, label: string][] = [
  ["auth.admin_denied", "Request"],
  ["auth.duplicate_cookie", "Host"],
  ["auth.password_code_sent", "To"],
  ["auth.signup_denied", "Email"],
  ["approval.", "Approval"],
  ["colleague.", "Request"],
  ["browser.", "Site"],
  ["skills.", "Skill"],
  ["mcp.", "Server"],
  ["webapp.", "App"],
  ["sync.connection_", "App"],
  ["sync.job_", "Job"],
  ["sync.run_started", "Job"],
  ["llm.provider_", "Provider"],
  ["sso.provider_", "Provider"],
  ["messaging.connector_saved", "App"],
  ["branding.", "Name"],
];

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** "keptPrivate" → "Kept private", "data-analysis" → "Data analysis". */
function words(key: string) {
  const s = key.replace(/([a-z\d])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const label = (key: string, action: string) => (key === "name" && action.startsWith("group.") ? "Group" : (LABELS[key] ?? words(key)));

function text(s: string, lookup: AuditLookup) {
  const known = lookup(s)?.name ?? NAMES[s];
  if (known) return known;
  if (/^\d{4}-\d\d-\d\dT\d\d:\d\d/.test(s) && !Number.isNaN(Date.parse(s))) return new Date(s).toLocaleString();
  if (/^[a-z]+(_[a-z]+)+$/.test(s)) return s.replaceAll("_", " "); // colleague_request → colleague request
  return s.length > 120 ? `${s.slice(0, 119)}…` : s;
}

function value(key: string, v: unknown, lookup: AuditLookup): string {
  if (v === null || v === undefined) return key === "monthlyBudgetUsd" ? "no limit" : "none";
  if (typeof v === "boolean") return YES_NO.has(key) || key.endsWith("Changed") ? (v ? "yes" : "no") : v ? "on" : "off";
  if (typeof v === "number") return UNITS[key]?.(v) ?? v.toLocaleString();
  if (typeof v === "string") return ((key === "publishSkills" || key === "promoteMemory") && LEVELS[v]) || text(v, lookup);
  if (Array.isArray(v)) {
    const items = v.map((x) => (typeof x === "string" ? text(x, lookup) : JSON.stringify(x)));
    if (!items.length) return "none";
    return items.length > 6 ? `${items.slice(0, 5).join(", ")} +${items.length - 5} more` : items.join(", ");
  }
  return JSON.stringify(v);
}

function fields(details: Record<string, unknown>, action: string, lookup: AuditLookup): AuditPart[] {
  return Object.entries(details).flatMap(([key, v]): AuditPart[] => {
    if (v === undefined || HIDDEN.has(key)) return [];
    // Time-saved standards: one entry per task type ("Writing: 25 min").
    if (key === "minutes" && isRecord(v)) return Object.entries(v).map(([t, m]) => ({ label: TASKS[t]?.label ?? words(t), value: value("minutes", m, lookup) }));
    // Settings saved as a block (a briefing, a sign-up policy) read as their own fields.
    if (isRecord(v)) return fields(v, action, lookup);
    return [{ label: label(key, action), value: value(key, v, lookup) }];
  });
}

/** Only what changed from `before` to `after`: "Browser: on → off", or what was added to and removed from a list. */
function changes(before: unknown, after: unknown, action: string, lookup: AuditLookup, key = ""): AuditPart[] {
  if (isRecord(before) && isRecord(after)) return Object.keys(before).filter((k) => k in after).flatMap((k) => changes(before[k], after[k], action, lookup, k));
  if (JSON.stringify(before) === JSON.stringify(after)) return [];
  if (!key && Array.isArray(before) && Array.isArray(after)) {
    const list = (xs: unknown[], name: string): AuditPart[] => (xs.length ? [{ label: name, value: value(name, xs, lookup) }] : []);
    return [...list(after.filter((x) => !before.includes(x)), "Added"), ...list(before.filter((x) => !after.includes(x)), "Removed")];
  }
  return [{ label: key ? label(key, action) : "Changed", value: `${value(key, before, lookup)} → ${value(key, after, lookup)}` }];
}

/** The thing acted on, when the page knows the id or the target is a name; not the actor's own account (or the one they acted as). */
function subject(row: AuditRow, after: unknown, actingAs: string | undefined, lookup: AuditLookup): AuditPart[] {
  const t = row.target;
  if (!t || t === row.actorId || t === actingAs) return [];
  const known = lookup(t);
  if (known) return [{ label: known.label, value: known.name }];
  if (row.action.startsWith("group.") && isRecord(after) && typeof after.name === "string") return [{ label: "Group", value: after.name }];
  const name = TARGETS.find(([a]) => row.action.startsWith(a))?.[1];
  return name ? [{ label: name, value: /^\d+$/.test(t) ? `#${t}` : text(t, lookup) }] : [];
}

export function auditParts(row: AuditRow, lookup: AuditLookup = () => undefined): AuditPart[] {
  const { impersonating, ...details } = isRecord(row.details) ? row.details : {};
  const parts: AuditPart[] = [];
  if ("before" in details && "after" in details) {
    const { before, after, ...rest } = details;
    parts.push(...changes(before, after, row.action, lookup), ...fields(rest, row.action, lookup));
  } else parts.push(...fields(details, row.action, lookup));
  // Done by an admin while acting as someone: the server records the admin as the actor and the account here.
  const actingAs = isRecord(impersonating) ? impersonating : undefined;
  if (actingAs) parts.push({ label: "Acting as", value: text(String(actingAs.email ?? actingAs.id), lookup) });
  // Details that repeat the subject (an invite's email, a new group's name) are left out, as are exact repeats.
  const head = subject(row, details.after, typeof actingAs?.id === "string" ? actingAs.id : undefined, lookup);
  const rest = parts.filter((p, i) => !head.some((h) => h.value === p.value) && parts.findIndex((q) => q.label === p.label && q.value === p.value) === i);
  return [...head, ...rest];
}
